import { Button, Spinner } from '@noorden-insurtech/alba-design-system';
import { formOptions } from '@tanstack/react-form';
import {
  ArrowLeft,
  FileDown,
  SquarePen,
} from 'lucide-react';
import { useQuoteForm } from '@/features/quotes/hooks/common/use-quote-form.hook';
import type { StepCalculationFormValues } from '@/features/quotes/schemas/capital-global/step-calculation.schema';
import { useStepCalculationViewModel } from './hooks/use-step-calculation-view-model.hook';
import { UnderAnalysisInfoModal } from '@/features/quotes/components/quote-steps/under-analysis-info-modal.component';
import {
  CalculationCommercializationSection,
  CalculationDataByStipulantSection,
  CalculationDataCardSection,
  CalculationInstallmentsTableSection,
  CalculationStipulantsListSection,
  CalculationSummaryCardSection,
} from './sections/step-calculation.sections';

const formOpts = formOptions({
  defaultValues: {} as StepCalculationFormValues,
});

type Props = {
  onNext: () => void;
  onBack: () => void;
  onResumeEdit?: () => void;
  canGoNext?: boolean;
  canGoBack?: boolean;
  isViewMode?: boolean;
};

export function StepCalculation({
  onNext: _onNext,
  onBack,
  onResumeEdit,
  canGoBack = true,
  isViewMode = false,
}: Props) {
  const form = useQuoteForm({ ...formOpts });
  const {
    calculationData,
    isLoading,
    isUnderAnalysis,
    isClosed,
    isTerminalStatus,
    showBlurredPremiums,
    isUnderAnalysisModalOpen,
    closeUnderAnalysisModal,
    handleCreateProposalClick,
    handleEditClick,
    handleExitClick,
    handleGeneratePdfClick,
    isCreateProposalPending,
    isGeneratePdfPending,
    canEditQuote,
    quoteWriteGuard,
    proposalWriteGuard,
    quoteActionGuard,
  } = useStepCalculationViewModel({
    form,
    onBack,
  });

  if (isLoading) {
    return (
      <div className='flex justify-center items-center py-16'>
        <Spinner size='lg' colorScheme='blue' />
      </div>
    );
  }

  return (
    <div className='flex flex-col gap-8'>
      <div className='flex justify-center'>
        <CalculationInstallmentsTableSection
          items={calculationData.installments}
          blurPremiumValues={showBlurredPremiums}
          showUnavailableMessage={showBlurredPremiums}
        />
      </div>

      <div className='grid grid-cols-1 gap-6 lg:grid-cols-2'>
        <CalculationSummaryCardSection data={calculationData.insuranceDataCard} />
        <CalculationSummaryCardSection data={calculationData.stipulantDataCard} />
      </div>

      <CalculationDataCardSection data={calculationData.calculationDataCard} />

      <CalculationDataByStipulantSection
        entries={calculationData.calculationDataByStipulant}
      />

      <CalculationStipulantsListSection entries={calculationData.stipulantAccordions} />

      <CalculationCommercializationSection data={calculationData.commercialization} />

      <div className='flex flex-col justify-between gap-2 md:flex-row md:items-center'>
        <Button
          variant='ghost'
          size='sm'
          className='flex items-center gap-2'
          onClick={onBack}
          disabled={isViewMode || !canGoBack}
        >
          <ArrowLeft className='h-5 w-5' strokeWidth={1.6} />
          Voltar
        </Button>

        <div className='flex flex-wrap justify-end gap-2'>
          {canEditQuote && (
            <Button
              variant='secondary'
              size='sm'
              className='shrink-0 group flex items-center justify-center gap-2 bg-alba-green-500 text-alba-white-500 [&>svg]:text-alba-white-500 hover:[&>svg]:text-[#129440] whitespace-nowrap'
              onClick={isViewMode ? onResumeEdit : handleEditClick}
              disabled={
                quoteWriteGuard.blocked ||
                (isViewMode ? !onResumeEdit : !canGoBack)
              }
              title={quoteWriteGuard.reason}
            >
              <SquarePen className='h-5 w-5' strokeWidth={2} />
              Editar
            </Button>
          )}

          <Button
            variant='secondary'
            size='sm'
            className='shrink-0 group flex items-center justify-center gap-2 bg-alba-green-500 text-alba-white-500 [&>svg]:text-alba-white-500 hover:[&>svg]:text-[#129440] whitespace-nowrap'
            loading={isGeneratePdfPending}
            disabled={
              isGeneratePdfPending ||
              isUnderAnalysis ||
              isTerminalStatus ||
              quoteActionGuard.blocked
            }
            title={quoteActionGuard.reason}
            onClick={() => {
              void handleGeneratePdfClick();
            }}
          >
            <FileDown className='h-5 w-5' strokeWidth={2} />
            Gerar PDF
          </Button>

          <Button
            variant='primary'
            size='sm'
            className='shrink-0 whitespace-nowrap'
            loading={isCreateProposalPending}
            disabled={
              isCreateProposalPending ||
              isUnderAnalysis ||
              isClosed ||
              proposalWriteGuard.blocked
            }
            title={proposalWriteGuard.reason}
            onClick={() => {
              void handleCreateProposalClick();
            }}
          >
            Criar Proposta
          </Button>

          <Button
            variant='primary'
            size='sm'
            className='shrink-0 whitespace-nowrap'
            onClick={handleExitClick}
          >
            Sair
          </Button>
        </div>
      </div>

      <UnderAnalysisInfoModal
        isOpen={isUnderAnalysisModalOpen}
        onClose={closeUnderAnalysisModal}
        confirmLabel='Ir para Minhas Cotações'
        onConfirm={handleExitClick}
      />
    </div>
  );
}
